import React from 'react';
import { useIdleTimerContext } from 'react-idle-timer';
import { useObserver } from 'mobx-react-lite';
import { useApolloClient } from '@apollo/client';
import styled from 'styled-components';
import Backdrop from '@material-ui/core/Backdrop';

import useCart from 'src/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import useStores from 'shared/hooks/use-stores';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { teardownKioskSession } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-session-teardown';

import { ModalWrapper } from 'shared/modals/v2-wrapper';
import { DesktopOnly, MobileOnly } from 'shared/components';

import { ModalContainer, BottomDrawer } from 'src/components/core';
import { withModalControlsHook, ModalComponentProps } from 'src/components/modals/with-modal-controls-hook';
import { KioskProIdleWarning } from 'src/components/modals/kiosk-pro-idle-warning';
import { IdleWarning } from './idle-warning';

const BlurBackdropComponent = styled(Backdrop)`
  &.MuiBackdrop-root {
    backdrop-filter: blur(12px) !important;
    -webkit-backdrop-filter: blur(12px) !important;
  }
`;

const IdleWarningModal = ({ onClose }: ModalComponentProps): JSX.Element => {
  const Cart = useCart();
  const UI = useUI();
  const { User, Modals } = useStores();
  const { dispensary } = useDispensary();
  const apolloClient = useApolloClient();
  const idleTimer = useIdleTimerContext();
  const dispoCname = dispensary?.cName;
  const isKioskProEnabled = useKioskPro();
  const isUserLoggedIn = useObserver(() => User?.isLoggedIn);

  const closeAndContinue = (): void => {
    onClose();
    idleTimer.reset();
  };

  const startNewSession = async (): Promise<void> => {
    onClose();
    if (isKioskProEnabled) {
      await teardownKioskSession({
        user: User,
        cart: Cart,
        ui: UI,
        apolloClient,
        idleTimer,
        dispensaryId: dispensary?.id,
        modals: Modals,
      });
    } else {
      UI.showKioskCheckoutModal = false;
      UI.setIsKioskProLoginModalOpen(false);
      UI.closeCartDrawer();
      UI.closeCartInterstitial();
      Cart.clearOrder();
      idleTimer.reset();
      if (isUserLoggedIn) {
        User?.logout();
      }
    }
    UI.redirectToKioskLockScreen(dispoCname);
  };

  return (
    <>
      <DesktopOnly mediaQuery='sm'>
        <ModalWrapper
          open
          onClose={closeAndContinue}
          {...(isKioskProEnabled ? { BackdropComponent: BlurBackdropComponent } : {})}
        >
          <ModalContainer
            width={isKioskProEnabled ? '100%' : '470px'}
            maxWidth={isKioskProEnabled ? '820px' : '470px'}
            p='32px'
          >
            {isKioskProEnabled ? (
              <KioskProIdleWarning closeAndContinue={closeAndContinue} startNewSession={() => void startNewSession()} />
            ) : (
              <IdleWarning closeAndContinue={closeAndContinue} startNewSession={() => void startNewSession()} />
            )}
          </ModalContainer>
        </ModalWrapper>
      </DesktopOnly>
      <MobileOnly mediaQuery='xs'>
        <BottomDrawer open onClose={closeAndContinue}>
          {isKioskProEnabled ? (
            <KioskProIdleWarning closeAndContinue={closeAndContinue} startNewSession={() => void startNewSession()} />
          ) : (
            <IdleWarning closeAndContinue={closeAndContinue} startNewSession={() => void startNewSession()} />
          )}
        </BottomDrawer>
      </MobileOnly>
    </>
  );
};

const [IdleWarningController, useIdleWarningModal] = withModalControlsHook(IdleWarningModal);

export { useIdleWarningModal, IdleWarningController, IdleWarningModal };
